import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { accountHeaders } from '@/lib/pwaAccount';
import { 
  UserPlus, Send, RotateCcw, Shield, ShieldOff, 
  Smartphone, CheckCircle, XCircle, Clock, AlertCircle 
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const EmployeePOSManagement = ({ restaurantId }) => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  const fetchEmployees = useCallback(async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API}/api/rest/v1/employees?restaurant_id=${restaurantId}`, {
        headers: { ...accountHeaders() }
      });
      const data = await res.json();
      setEmployees(data.employees || []);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const getPOSStatusBadge = (status) => {
    const badges = {
      'inactive': { color: 'bg-gray-100 text-gray-600', icon: ShieldOff, text: 'No POS Access' },
      'pending_pos_setup': { color: 'bg-yellow-100 text-yellow-700', icon: Clock, text: 'Setup Pending' },
      'pos_active': { color: 'bg-green-100 text-green-700', icon: CheckCircle, text: 'POS Active' },
      'pos_revoked': { color: 'bg-red-100 text-red-700', icon: XCircle, text: 'Revoked' },
    };
    
    const badge = badges[status] || badges.inactive;
    const Icon = badge.icon;
    
    return (
      <Badge className={`${badge.color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {badge.text}
      </Badge>
    );
  };

  if (loading) {
    return <div className="text-center py-8">Loading employees...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Employee POS Access</h2>
          <p className="text-gray-600 text-sm">Manage staff POS access and device security</p>
          {/* the same allow list (and the same server door) as Back Office -> Staff & Labor -> POS Login Access */}
          <p className="text-gray-600 text-xs mt-1">
            Only phones allowed here (the same list as Back Office &rarr; Staff &amp; Labor &rarr; POS Login Access) can
            sign in to the POS. Turning access off signs that phone out on every device.
          </p>
        </div>
        <Button onClick={() => setShowAddModal(true)} className="flex items-center gap-2">
          <UserPlus className="w-4 h-4" />
          Add Employee
        </Button>
      </div>

      {/* Employees List */}
      <div className="grid gap-4">
        {employees.map(employee => (
          <EmployeeCard 
            key={employee.id} 
            employee={employee} 
            onUpdate={fetchEmployees}
            getPOSStatusBadge={getPOSStatusBadge}
          />
        ))}
      </div>

      {showAddModal && (
        <AddEmployeeModal 
          restaurantId={restaurantId}
          onClose={() => setShowAddModal(false)}
          onSuccess={fetchEmployees}
        />
      )}
    </div>
  );
};

const EmployeeCard = ({ employee, onUpdate, getPOSStatusBadge }) => {
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState('');

  const togglePOSAccess = async (enabled) => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API}/api/rest/v1/employees/${employee.id}/pos-access`, {
        method: 'PUT',
        headers: { 
          ...accountHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ enabled })
      });
      
      const data = await res.json();
      
      if (data.success) {
        setMessage(enabled ? 'POS access enabled' : 'POS access disabled');
        setTimeout(() => setMessage(''), 3000);
        onUpdate();
      } else {
        setMessage(data.message || 'Failed to update');
      }
    } catch (error) {
      setMessage('Error updating POS access');
    }
  };

  const sendSetupLink = async () => {
    setSending(true);
    setMessage('');
    
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API}/api/rest/v1/employees/${employee.id}/send-pos-setup-link`, {
        method: 'POST',
        headers: { ...accountHeaders() }
      });
      
      const data = await res.json();
      
      if (data.success) {
        setMessage('✅ Setup link sent via SMS');
        setTimeout(() => setMessage(''), 5000);
        onUpdate();
      } else {
        setMessage(data.message || 'Failed to send');
      }
    } catch (error) {
      setMessage('Error sending setup link');
    } finally {
      setSending(false);
    }
  };

  const resetPIN = async () => {
    if (!window.confirm(`Reset PIN for ${employee.full_name}? They will need to set up again.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API}/api/rest/v1/employees/${employee.id}/reset-pos-pin`, {
        method: 'POST',
        headers: { ...accountHeaders() }
      });
      
      const data = await res.json();
      setMessage(data.success ? '✅ PIN reset' : 'Failed to reset');
      setTimeout(() => setMessage(''), 3000);
      onUpdate();
    } catch (error) {
      setMessage('Error resetting PIN');
    }
  };

  const revokeDevices = async () => {
    if (!window.confirm(`Revoke all devices for ${employee.full_name}?`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API}/api/rest/v1/employees/${employee.id}/revoke-pos-devices`, {
        method: 'POST',
        headers: { ...accountHeaders() }
      });
      
      const data = await res.json();
      setMessage(data.success ? '✅ Devices revoked' : 'Failed');
      setTimeout(() => setMessage(''), 3000);
      onUpdate();
    } catch (error) {
      setMessage('Error revoking devices');
    }
  };

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          {/* Employee Info */}
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="text-lg font-semibold">{employee.full_name}</h3>
              {getPOSStatusBadge(employee.pos_status || 'inactive')}
            </div>
            
            <div className="space-y-1 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <span>📧 {employee.email}</span>
              </div>
              {employee.phone && (
                <div className="flex items-center gap-2">
                  <Smartphone className="w-4 h-4" />
                  {employee.phone}
                </div>
              )}
              <div className="flex items-center gap-2">
                <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-medium">
                  {employee.role}
                </span>
              </div>
            </div>

            {employee.last_pos_login_at && (
              <p className="text-xs text-gray-500 mt-2">
                Last POS login: {new Date(employee.last_pos_login_at).toLocaleString()}
              </p>
            )}

            {message && (
              <div className="mt-3 p-2 bg-blue-50 text-blue-700 rounded text-sm">
                {message}
              </div>
            )}
          </div>

          {/* POS Access Controls */}
          <div className="flex flex-col gap-3 ml-6">
            {/* Enable/Disable POS Access */}
            <div className="flex items-center gap-2">
              <Switch 
                checked={employee.pos_access_enabled} 
                onCheckedChange={togglePOSAccess}
              />
              <Label className="text-sm font-medium cursor-pointer">
                POS Access
              </Label>
            </div>

            {/* Action Buttons */}
            {employee.pos_access_enabled && (
              <div className="flex flex-col gap-2">
                {employee.pos_status === 'pending_pos_setup' && (
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={sendSetupLink}
                    disabled={sending || !employee.phone}
                    className="w-full flex items-center justify-center gap-2"
                  >
                    <Send className="w-3 h-3" />
                    {sending ? 'Sending...' : 'Send Setup Link'}
                  </Button>
                )}

                {employee.pos_status === 'pos_active' && (
                  <>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={resetPIN}
                      className="w-full flex items-center justify-center gap-2"
                    >
                      <RotateCcw className="w-3 h-3" />
                      Reset PIN
                    </Button>

                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={revokeDevices}
                      className="w-full flex items-center justify-center gap-2"
                    >
                      <Shield className="w-3 h-3" />
                      Revoke Devices
                    </Button>
                  </>
                )}
              </div>
            )}

            {!employee.phone && employee.pos_access_enabled && (
              <div className="flex items-center gap-1 text-xs text-amber-600">
                <AlertCircle className="w-3 h-3" />
                <span>Phone required</span>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const AddEmployeeModal = ({ restaurantId, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    role: 'waiter',
    pos_access_enabled: false,
    password: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API}/api/rest/v1/employees`, {
        method: 'POST',
        headers: { 
          ...accountHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          restaurant_id: restaurantId
        })
      });
      
      const data = await res.json();
      
      if (data.success) {
        onSuccess();
        onClose();
      } else {
        setError(data.message || 'Failed to create employee');
      }
    } catch (error) {
      setError('Error creating employee');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Add New Employee</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Full Name *</Label>
              <Input 
                required
                value={formData.full_name}
                onChange={(e) => setFormData({...formData, full_name: e.target.value})}
              />
            </div>

            <div>
              <Label>Email *</Label>
              <Input 
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
              />
            </div>

            <div>
              <Label>Phone (Required for POS)</Label>
              <Input 
                type="tel"
                placeholder="+1234567890"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
              />
            </div>

            <div>
              <Label>Password *</Label>
              <Input 
                type="password"
                required
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
              />
            </div>

            <div>
              <Label>Role *</Label>
              <select 
                className="w-full border rounded px-3 py-2"
                value={formData.role}
                onChange={(e) => setFormData({...formData, role: e.target.value})}
              >
                {/* the staff roles of the ONE permission catalog (backend/security/permission_catalog.py) */}
                <option value="waiter">Waiter</option>
                <option value="host">Host</option>
                <option value="cashier">Cashier</option>
                <option value="kitchen">Kitchen</option>
                <option value="expo">Expo</option>
                <option value="manager">Manager</option>
              </select>
            </div>

            <div className="flex items-center gap-2 p-3 bg-blue-50 rounded">
              <Switch 
                checked={formData.pos_access_enabled}
                onCheckedChange={(checked) => setFormData({...formData, pos_access_enabled: checked})}
              />
              <Label className="cursor-pointer">
                Enable POS Access (will send setup SMS)
              </Label>
            </div>

            {error && (
              <div className="p-3 bg-red-50 text-red-700 rounded text-sm">
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={onClose} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" disabled={saving} className="flex-1">
                {saving ? 'Creating...' : 'Create Employee'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmployeePOSManagement;
